import { defineStore } from 'pinia';
import { MessagePlugin, NotifyPlugin } from 'tdesign-vue-next';
import dayjs from 'dayjs';
import { client, headers } from '~/utils/upLoad.js';
import { addAudio } from '~/utils/indexedDB.js';
import { trackEvent } from '~/utils/analytics.js';
import { isDingTalkEnvironment } from '~/utils/device-detect.js';
import { getSupportedMimeType, getFileExtension } from '~/utils/audio-transcode.js';

export const audioStore = defineStore({
    id: 'audioStore',
    state: () => ({
        //记录当前面试录音的chunks
        chunks:[],
        //定义录音音轨
        mediaRecorder: null,
        //记录当前面试录音的blob（用于上传失败后重试）
        audioBlob: null,
        //记录正在面试的节点的id
        isRecordingNodeId: null,
        //记录正在面试方案的id
        isRecordingProcessId: null,
        //记录是否正在上传录音
        uploading: false,
        //记录上传进度
        uploadProgress: 0,
        //记录面试的时长:秒
        seconds: 0,
        //计时器
        timer: null,
        //记录计时状态
        isTimerRunning: false,
        //关闭录音的弹窗
        isStopOpen:false,
        //定义切片的的录音
        adviseChunks:[],
        //记录录音状态
        isRecording: false,
        //记录暂停状态
        isPaused: false,
        //记录音频流
        audioStream: null,
        //记录录音使用的 MIME 类型
        recordingMimeType: null,
    }),
    
    actions: {
        /**
         * 开始计时
         * 如果计时器已经存在，则继续计时；否则创建新的计时器
         */
        startTimer() {
            // 如果计时器已经在运行，则不需要再次启动
            if (this.isTimerRunning) return this.timer;
            
            // 创建新的计时器
            this.timer = setInterval(() => {
                this.seconds++;
                // console.log('计时器正在运行，当前秒数：', this.seconds);
                
            }, 1000);
            
            this.isTimerRunning = true;
            return this.timer;
        },
        
        /**
         * 暂停计时
         * 暂停计时器但保留当前计时值，可以之后继续
         */
        pauseTimer() {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
                this.isTimerRunning = false;
            }
        },
        
        /**
         * 继续计时
         * 在当前计时值的基础上继续计时
         */
        resumeTimer() {
            // 如果计时器已经在运行，则不需要再次启动
            if (this.isTimerRunning) return this.timer;
            
            // 创建新的计时器，继续计时
            this.timer = setInterval(() => {
                this.seconds++;
            }, 1000);
            
            this.isTimerRunning = true;
            return this.timer;
        },
        
        /**
         * 重置计时器
         * 清除计时器并重置计时值
         */
        resetTimer() {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
            this.seconds = 0;
            this.isTimerRunning = false;
        },
        
        /**
         * 获取格式化的时间
         * 返回 HH:MM:SS 格式的时间字符串
         */
        getFormattedTime() {
            const hrs = Math.floor(this.seconds / 3600);
            const mins = Math.floor((this.seconds % 3600) / 60);
            const secs = this.seconds % 60;
            
            return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        },

        /**
         * 检查麦克风权限
         * 返回权限状态：granted, denied, prompt
         */
        async checkMicrophonePermission() {
            try {
                const permission = await navigator.permissions.query({ name: 'microphone' });
                return permission.state;
            } catch (error) {
                console.error('检查麦克风权限失败:', error);
                return 'denied';
            }
        },

        /**
         * 开始录音
         * @param {Object} params - 录音参数
         * @param {string} params.nodeId - 节点ID
         * @param {string} params.processId - 流程ID  
         * @param {Object} params.nodeData - 节点数据
         * @param {Function} params.onSuccess - 成功回调
         * @param {Function} params.onError - 失败回调
         * @param {Function} params.updateNodeTime - 更新节点时间回调
         * @param {Function} params.trackEventFn - 事件跟踪函数
         * @param {Function} params.updateStoreStatus - 更新其他store状态回调
         */
        async startRecording({
            nodeId,
            processId,
            nodeData,
            onSuccess,
            onError,
            updateNodeTime,
            trackEventFn,
            updateStoreStatus
        }) {
            try {
                // 先检查麦克风权限
                const permission = await navigator.permissions.query({ name: 'microphone' });
                let warningPlugin = null;
                
                // 监听权限变化
                permission.addEventListener('change', (e) => {
                    if (e.target.state === 'granted') {
                        if (warningPlugin) {
                            MessagePlugin.close(warningPlugin);
                        }
                    } else if (e.target.state === 'denied') {
                        // 用户在录音过程中关闭了权限
                        if (this.isRecording) {
                            MessagePlugin.error('麦克风权限被关闭，请打开录音权限');
                            this.pauseRecording();
                        }
                    }
                });
                console.log("看看这个状态是什么",permission);
                console.log("看看这个状态是什么",permission.state);
                // 根据权限状态显示不同提示
                if (permission.state === 'prompt') {

                    // 判断是否在钉钉环境
                    const isDingTalk = isDingTalkEnvironment()
                    console.log('是否在钉钉环境:', isDingTalk)
                    if(!isDingTalk){
                        warningPlugin = MessagePlugin.warning({
                            content: '请在浏览器弹窗中点击"允许"以开启麦克风权限',
                            duration: 5000,
                            closeBtn: true,
                            icon: true
                        });
                    }
                    
                    // 监听权限状态变化
                    permission.addEventListener('change', (e) => {
                        if (e.target.state === 'granted') {
                            MessagePlugin.close(warningPlugin);
                            NotifyPlugin.success({
                                title: '正在录音',
                                content: '录音过程中，请勿切换至其他页面，以免录音文件丢失',
                                duration: 3000,
                                closeBtn: true,
                            });
                        }
                    });
                }
                
                if (permission.state === 'denied') {
                    MessagePlugin.error('请允许访问麦克风后再开始录音');
                    onError?.();
                    return;
                }
                
                if (permission.state === 'granted') {
                    // 用户已经允许访问麦克风，直接开始录音
                    NotifyPlugin.success({
                        title: '正在录音',
                        content: '录音过程中，请勿切换至其他页面，以免录音文件丢失',
                        duration: 3000,
                        closeBtn: true,
                    });
                }

                // 请求麦克风权限并获取音频流
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                this.audioStream = stream;

                // 选择浏览器支持的录音格式（避免把 webm/ogg 当成 mp3 导致后端解码/转写异常）
                const supportedMimeType = getSupportedMimeType();
                this.recordingMimeType = supportedMimeType || null;
                
                // 重置计时器并开始计时
                this.seconds = 0;
                this.startTimer();
                
                // 调用更新节点时间的回调
                if (updateNodeTime) {
                    await updateNodeTime();
                }

                this.mediaRecorder = supportedMimeType
                    ? new MediaRecorder(stream, { mimeType: supportedMimeType })
                    : new MediaRecorder(stream);
                this.chunks = [];
                this.isRecordingNodeId = nodeId;
                this.isRecordingProcessId = processId;
                this.isRecording = true;
                this.isPaused = false;

                // 监听 dataavailable 事件来收集录音数据块
                this.mediaRecorder.addEventListener('dataavailable', (event) => {
                    if (event.data && event.data.size > 0) {
                        this.chunks.push(event.data);
                        console.log('收集到录音数据块，大小：', event.data.size, '字节');
                    }
                });

                // 更新其他store状态
                if (updateStoreStatus) {
                    updateStoreStatus('recording');
                }
                
                // 存储录音状态
                localStorage.setItem('recordingStatus', 'recording');

                // 开始录音（每1秒生成一次数据块）
                this.mediaRecorder.start(1000);
                
                // 触发成功回调
                onSuccess?.();
                
                // 事件跟踪
                if (trackEventFn) {
                    trackEventFn();
                }

            } catch (error) {
                console.error("Error accessing microphone:", error);
                
                // 根据错误类型显示不同的提示
                if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                    MessagePlugin.error("请在浏览器设置中允许访问麦克风后重试");
                } else {
                    MessagePlugin.error("无法访问麦克风，请检查设备连接");
                }

                // 清理状态
                this.isRecording = false;
                this.isPaused = false;
                
                // 触发失败回调
                onError?.(error);
            }
        },

        /**
         * 暂停录音
         * @param {string} nodeId - 可选，请求暂停的节点ID，用于验证权限
         */
        pauseRecording(nodeId) {
            // 如果提供了nodeId，验证是否是当前录音的节点
            if (nodeId && nodeId !== this.isRecordingNodeId) {
                console.warn('只有正在录音的节点才能暂停录音', {
                    requestNodeId: nodeId,
                    recordingNodeId: this.isRecordingNodeId
                });
                MessagePlugin.warning('无法暂停其他节点的录音');
                return false;
            }
            
            if (this.mediaRecorder && this.isRecording && !this.isPaused) {
                this.mediaRecorder.pause();
                this.isPaused = true;
                this.pauseTimer();
                sessionStorage.setItem('pauseTime', new Date().getTime());
                //输出下当前录音状态

                return true;
            }
            return false;
        },

        /**
         * 继续录音
         * @param {string} nodeId - 可选，请求继续的节点ID，用于验证权限
         */
        resumeRecording(nodeId) {
            // 如果提供了nodeId，验证是否是当前录音的节点
            if (nodeId && nodeId !== this.isRecordingNodeId) {
                console.warn('只有正在录音的节点才能继续录音', {
                    requestNodeId: nodeId,
                    recordingNodeId: this.isRecordingNodeId
                });
                MessagePlugin.warning('无法继续其他节点的录音');
                return false;
            }
            
            if (this.mediaRecorder && this.isPaused) {
                this.mediaRecorder.resume();
                this.isPaused = false;
                this.resumeTimer();
                return true;
            }
            return false;
        },

        /**
         * 停止录音（基础方法）
         * @param {string} nodeId - 可选，请求停止的节点ID，用于验证权限
         * @returns {Promise<Blob>} 返回录音的Blob对象
         */
        async stopRecording(nodeId) {
            // 如果提供了nodeId，验证是否是当前录音的节点
            if (nodeId && nodeId !== this.isRecordingNodeId) {
                console.warn('只有正在录音的节点才能停止录音', {
                    requestNodeId: nodeId,
                    recordingNodeId: this.isRecordingNodeId
                });
                MessagePlugin.warning('无法停止其他节点的录音');
                return null;
            }

            if (!this.mediaRecorder || !this.isRecording) {
                MessagePlugin.error('生成录音文件失败');
                return null;
            }

            try {
                // 创建一个Promise来等待stop事件完成
                const stopPromise = new Promise((resolve) => {
                    // 添加一次性事件监听器
                    this.mediaRecorder.addEventListener("stop", async () => {
                        // 使用录音时实际的 MIME 类型（不要硬编码为 mp3）
                        const actualMimeType = this.recordingMimeType || this.mediaRecorder?.mimeType || '';
                        console.log('创建录音 Blob，使用格式:', actualMimeType || '(default)');

                        const audioBlob = new Blob(this.chunks, actualMimeType ? { type: actualMimeType } : undefined);
                        
                        // 解析Promise，表示stop事件处理完成
                        resolve(audioBlob);
                    }, { once: true }); // 使用once选项确保事件只触发一次
                });

                // 尽量在 stop 前把最后一段数据 flush 出来，避免只剩“几秒钟”
                try {
                    if (this.mediaRecorder && this.mediaRecorder.state === 'recording' && typeof this.mediaRecorder.requestData === 'function') {
                        this.mediaRecorder.requestData();
                    }
                } catch (e) {
                    // ignore
                }
                
                // 停止录音
                this.mediaRecorder.stop();
                this.isRecording = false;
                this.isPaused = false;
                
                // 停止所有音轨
                if (this.audioStream) {
                    this.audioStream.getTracks().forEach((track) => track.stop());
                    this.audioStream = null;
                }
                
                // 等待stop事件处理完成并获取audioBlob
                const blob = await stopPromise;
                
                return blob;
                
            } catch (error) {
                console.error('停止录音失败:', error);
                MessagePlugin.error('停止录音失败');
                
                // 清理状态
                this.isRecording = false;
                this.isPaused = false;
                
                return null;
            }
        },

        /**
         * 停止录音并完成整个上传流程（完整版）
         * @param {Object} params - 参数对象
         * @param {Object} params.interviewCourseStore - 面试流程 Store 实例
         * @param {Object} params.interviewStore - 面试 Store 实例
         * @param {Object} params.router - Vue Router 的 router 对象
         * @param {Function} params.clearStoredAudio - 清除 IndexedDB 数据的函数
         * @param {Function} params.patchRecordNoteMain - 更新记录笔记的函数（可选）
         * @param {Function} params.onStateUpdate - 状态更新回调（用于更新组件状态）
         * @returns {Promise<{success: boolean, error?: any}>}
         */
        async stopRecordingAndUpload({
            interviewCourseStore,
            interviewStore,
            router,
            clearStoredAudio,
            patchRecordNoteMain,
            onStateUpdate
        }) {
            try {
                // 1. 使用 store 中已记录的 nodeId 和 processId
                const nodeId = this.isRecordingNodeId;
                const processId = this.isRecordingProcessId;

                if (!nodeId || !processId) {
                    MessagePlugin.error('录音信息不完整，无法停止录音');
                    return { success: false, error: '录音信息不完整' };
                }

                // 2. 停止录音，获取 Blob（仅在第一次调用时停止录音）
                let blob = this.audioBlob;
                let isFirstTime = false; // 标记是否是第一次停止录音
                
                if (!blob) {
                    // 第一次调用，需要停止录音并获取 blob
                    isFirstTime = true;
                    onStateUpdate?.({ loading: true, status: '正在停止录音...' });
                    
                    blob = await this.stopRecording(nodeId);
                    
                    if (!blob) {
                        throw new Error('未能创建录音文件');
                    }
                    
                    console.log('录音已停止，blob 大小：', blob.size, '字节，类型：', blob.type);
                    
                    // 保存 blob 到 store，以便上传失败后重试
                    this.audioBlob = blob;
                } else {
                    // 重试上传，使用已保存的 blob
                    console.log('使用已保存的录音 blob 重新上传，大小：', blob.size, '字节');
                    onStateUpdate?.({ loading: true, status: '正在重新上传...' });
                }

                // 更新组件状态
                onStateUpdate?.({ 
                    audioBlob: blob,
                    audioUrl: URL.createObjectURL(blob),
                    status: '录音完成',
                    isRecording: false,
                    isPaused: false
                });

                // 3. 保存到 IndexedDB（仅第一次）
                if (isFirstTime && processId && nodeId) {
                    try {
                        await addAudio({
                            interviewId: processId,
                            nodeId: nodeId,
                            file: blob
                        });
                        console.log('录音已保存到 IndexedDB（如果已存在会自动替换）', {
                            interviewId: processId,
                            nodeId: nodeId,
                            size: blob.size
                        });
                    } catch (dbError) {
                        console.error("Failed to store audio in IndexedDB:", dbError);
                        MessagePlugin.error('本地保存录音文件失败');
                    }
                }

                // 4. 上传到 OSS
                onStateUpdate?.({ status: '正在上传...' });
                
                const uploadResult = await this._uploadToOSS({
                    blob,
                    processId,
                    nodeId,
                    interviewStore
                });

                if (!uploadResult.success) {
                    throw new Error(uploadResult.error || '上传失败');
                }

                // 5. 创建离线任务
                onStateUpdate?.({ status: '正在创建任务...' });
                
                const taskResult = await this._createOfflineTask({
                    ossUrl: uploadResult.ossUrl,
                    nodeId,
                    interviewStore
                });

                if (!taskResult.success) {
                    throw new Error('创建离线任务失败');
                }

                // 6. 更新记录和时长
                if (patchRecordNoteMain) {
                    await patchRecordNoteMain();
                }
                
                await this._saveInterviewTime({
                    nodeId,
                    seconds: this.seconds,
                    interviewCourseStore
                });

                // 7. 更新节点状态
                interviewCourseStore.mitNodeData.nodeStatus = 5;
                interviewCourseStore.mitNodeData.taskId = taskResult.taskId;
                // interviewCourseStore.showConclusionView = true;
                
                if (interviewCourseStore.flowStepList.length > 0) {
                    const step = interviewCourseStore.flowStepList.find(item => item.id === nodeId);
                    if (step) {
                        step.nodeStatus = 5;
                    }
                }

                // 8. 清理资源和状态
                if (clearStoredAudio) {
                    clearStoredAudio();
                }
                // 跳转到总结页
                router.push({
                    path: "/interview-flow/conclusion",
                    query: {
                        interviewId: processId,
                        nodeId: nodeId
                    }
                });
                //等待2s
                await new Promise(resolve => setTimeout(resolve, 500));
                // 清理 audio-store 的录音状态
                this.cleanupRecording();
                // 清理 interview-course-store 的面试状态
                interviewCourseStore.cleanupInterviewState();

                // 9. 事件跟踪
                const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
                trackEvent('smart_interview_end', {
                    category: '面试',
                    label: '结束一次智能面试',
                    user_id: userInfo.userId || 'unknown',
                    tenant_id: userInfo.tenantId || 'unknown',
                    interview_progress_id: processId,
                    interview_node_id: nodeId,
                    create_timestamp: new Date().toISOString(),
                });

                // 10. 更新最终状态
                onStateUpdate?.({ 
                    status: '上传成功！',
                    loading: false
                });

             
                this.uploadProgress=0;
                return { success: true };

            } catch (error) {
                console.error('停止录音流程失败:', error);
                // 上传失败时，确保录音数据已保存到 IndexedDB
                const nodeId = this.isRecordingNodeId;
                const processId = this.isRecordingProcessId;
                const blob = this.audioBlob;
                
                if (blob && processId && nodeId) {
                    try {
                        // 使用 put 方法保存，如果已存在会自动替换为最新的录音数据
                        await addAudio({
                            interviewId: processId,
                            nodeId: nodeId,
                            file: blob
                        });
                        console.log('上传失败，录音已更新到 IndexedDB，可以稍后重试', {
                            interviewId: processId,
                            nodeId: nodeId,
                            size: blob.size
                        });
                        // MessagePlugin.warning('上传失败，录音已保存到本地，请稍后重试');
                    } catch (dbError) {
                        console.error('保存录音到 IndexedDB 失败:', dbError);
                        // MessagePlugin.error('录音保存失败，请检查浏览器存储空间');
                    }
                } else {
                    MessagePlugin.error(error.message || '处理录音文件失败');
                }
                
                // 不清理录音状态，保留 audioBlob 以便重试
                this.uploading = false;
                
                onStateUpdate?.({ 
                    loading: false,
                    status: '上传失败'
                });
                let data = JSON.stringify({
                    "status": "error",
                    "logs": "停止录音流程失败" + ",UserId:" + localStorage.getItem('userInfo')?.userId,
                    "errorMessage": JSON.stringify(error),
                    "service": "interview",
                })
                console.error('停止录音流程失败:', error);
                fetch("https://alert-api.itasaas.com/log", {
                    headers: {
                        "accept": "application/json, text/plain, */*",
                        "content-type": "application/json"
                    },
                    method: "POST",
                    body: data,
                })
                .then(response => response.json())
                .then(result => {
                    console.log('停止录音流程失败', result);
                })
                .catch(error => console.log('停止录音流程失败', error));
                
                return { success: false, error: error.message };
            }
        },

        /**
         * 上传到 OSS（内部方法）
         * @private
         * @param {Object} options - 上传参数
         * @param {Blob} options.blob - 音频文件Blob
         * @param {string} options.processId - 流程ID
         * @param {string} options.nodeId - 节点ID
         * @param {Object} options.interviewStore - 面试store实例
         * @param {string} options.customFileName - 自定义文件名（可选），如果不提供则根据 MIME 类型自动生成
         */
        async _uploadToOSS({ blob, processId, nodeId, interviewStore, customFileName }) {
            try {
                // 1. 获取 OSS 凭证
                const res = await interviewStore.getStsCredential();
                
                if (res.code !== 200) {
                    throw new Error('获取OSS凭证失败');
                }

                // 2. 创建 OSS 客户端（使用导入的 client 函数）
                const { accessKeyId, accessKeySecret, securityToken } = res.data.credentials;
                const ossClient = client(accessKeyId, accessKeySecret, securityToken);

                // 3. 上传文件
                this.uploading = true;
                this.uploadProgress = 0;

                // 根据 blob 的实际类型确定文件扩展名（不要默认为 mp3）
                const inferredExt = getFileExtension(blob?.type || this.recordingMimeType || '');
                const fileExt = customFileName
                    ? (customFileName.split('.').pop() || inferredExt)
                    : inferredExt;
                const fileBaseName = customFileName || `recording.${fileExt}`;
                const fileName = `interview/process/${processId}/nodes/${nodeId}/${fileBaseName}`;
                
                console.log('上传文件:', fileName, '格式:', blob.type);
                
                // 设置60秒超时
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('上传超时')), 60000);
                });

                // 使用导入的 headers
                const uploadPromise = ossClient.multipartUpload(fileName, blob, {
                    headers,
                    progress: (p) => {
                        this.uploadProgress = Math.round(p * 100);
                        console.log('上传进度：', p * 100 + '%');
                    }
                });

                const result = await Promise.race([uploadPromise, timeoutPromise]);

                if (!result) {
                    throw new Error('上传失败');
                }

                const ossUrl = `https://${result.bucket}.oss-cn-shenzhen.aliyuncs.com/${result.name}`;

                this.uploading = false;

                return { success: true, ossUrl };

            } catch (error) {
                this.uploading = false;
                if (error.message === '上传超时') {
                    MessagePlugin.error("上传超时");
                } else if (error.name === 'TypeError' || error.message.includes('network')) {
                    MessagePlugin.error("网络连接已断开，请检查网络");
                } else {
                    MessagePlugin.error("上传录音失败");
                }
                let data = JSON.stringify({
                    "status": "error",
                    "logs": "OSS上传失败" + ",UserId:" + localStorage.getItem('userInfo')?.userId,
                    "errorMessage": JSON.stringify(error),
                    "service": "interview",
                })
                console.error('OSS上传失败:', error);
                fetch("https://alert-api.itasaas.com/log", {
                    headers: {
                        "accept": "application/json, text/plain, */*",
                        "content-type": "application/json"
                    },
                    method: "POST",
                    body: data,
                })
                .then(response => response.json())
                .then(result => {
                    console.log('OSS上传失败', result);
                })
                .catch(error => console.log('OSS上传失败', error));
                return { success: false, error: error.message };
            }
        },

        /**
         * 创建离线任务（内部方法）
         * @private
         */
        async _createOfflineTask({ ossUrl, nodeId, interviewStore }) {
            try {
                const data = {
                    url: ossUrl,
                    nodeId: nodeId
                };
                
                // const res = await interviewStore.uploadAliOffline(data);
                const res = await interviewStore.uploadAudioSubmit(data);
                
                if (res.code === 200) {
                    return { 
                        success: true, 
                        taskId: res.data 
                    };
                } else {
                    throw new Error('创建离线任务失败');
                }
            } catch (error) {
                console.error('创建离线任务失败:', error);
                return { success: false, error: error.message };
            }
        },

        /**
         * 保存面试时长（内部方法）
         * @private
         */
        async _saveInterviewTime({ nodeId, seconds, interviewCourseStore }) {
            try {
                const data = {
                    id: nodeId,
                    name: interviewCourseStore.mitNodeData.name,
                    assigneeId: interviewCourseStore.mitNodeData.assigneeId,
                    category: interviewCourseStore.mitNodeData.category,
                    interviewInterval: Math.floor(seconds / 60),
                };
                
                await interviewCourseStore.editBusinessProcessNode(data);
            } catch (error) {
                console.error('保存面试时长失败:', error);
            }
        },

        /**
         * 清理录音资源
         * 重置所有录音相关状态
         */
        cleanupRecording() {
            // 停止计时器
            this.resetTimer();
            // 清理录音状态
            this.chunks = [];
            this.adviseChunks = [];
            this.audioBlob = null; // 清理保存的 blob
            this.mediaRecorder = null;
            this.isRecording = false;
            this.isPaused = false;
            this.uploading = false;
            this.uploadProgress = 0;
            this.recordingMimeType = null;
            
            // 停止音频流
            if (this.audioStream) {
                this.audioStream.getTracks().forEach((track) => track.stop());
                this.audioStream = null;
            }
            this.isRecordingNodeId = null;
            this.isRecordingProcessId = null;
            
            // 清理本地存储
            localStorage.removeItem('recordingStatus');
        }
    }
})
